import { FC, useEffect, useState } from 'react';
import { StyledWelcomeDetail } from './WelcomeDetail.styles';
import { WelcomeDetailProps } from './WelcomeDetail.types';
import { SurveyWrapper } from '../../molecules/SurveyWrapper';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, Text, Types } from '@marriott/mi-ui-library';
import { ErrorDetail } from '../Error';
import dayjs from 'dayjs';
import { brandColorSchemeGenerator } from '../../util/constants';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import DOMPurify from 'isomorphic-dompurify';
import { capitalize } from '../../util/helpers';
import { SurveyWrapperInner } from '../../molecules/SurveyWrapperInner';
import { validateStayNotStarted } from '../../util/prearrivalValidation';

export const WelcomeDetail: FC<WelcomeDetailProps> = (props: WelcomeDetailProps) => {
  const { formData, setFormData } = useCreateSurveyStore();
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const marshaCode: any = searchParams.get('marshaCode');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const confirmationNumber1: any = searchParams.get('confirmationNumber');

  const welcomeI18nText: string = props?.i18nMap?.welcome ?? 'Welcome';

  const { isShowError, errorMsg, errorInfo } = validateStayNotStarted({
    arrivalDate: props?.reservation?.arrival,
    isAuthorMode: props.isAuthorMode ?? false,
    confirmationNumber: confirmationNumber1,
    marshaCode: marshaCode,
    errorData: props?.errorData,
    propertyConfigError: props?.property_config?.error,
    errorMessage: props?.errorMessage,
  });

  const welcomeText = props?.model?.welcomeText;

  const hotelName = `${props?.propertyName}`;
  const updateHotelName = welcomeText?.replace('%hotel_name%', hotelName);
  const updatedDescription = updateHotelName?.replace('%reservation_no%', props?.reservation?.confirmationNumber);
  const ResconfirmationNumber = props?.reservation?.confirmationNumber;
  const RescurrentDate = dayjs(props?.reservation?.confoCreate || dayjs()).format('MMDDYYYY');

  const combinedValue = `${RescurrentDate}${ResconfirmationNumber}`;

  const alternateCID = props?.reservation?.AlternateCustomerId;

  const [customerName, setCustomerName] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updatedDetails: any = [];

  updatedDetails.push({
    contact_method_type: 'email',
    contact_method_detail: props?.reservation?.cusotmerEmail,
    timezone_of_guest: '', // Add logic to set the correct timezone if needed
  });

  const handleNextPage = (newPart?: string) => {
    // save the marsha code and confirmation number in session storage
    setFormData({
      ...formData,
      marshaCode: marshaCode,
      confirmationNumber: confirmationNumber1,
      amenities: props?.amenities,
      property_config: props?.property_config,
      reservation: props?.reservation,
      passions: props?.passions,
      marsha_cd: marshaCode,
      reservation_num: props?.reservation?.confirmationNumber,
      preferred_contact_method_details: updatedDetails,
      favIcon: props?.favIcon,
      brandCode: props?.brandCode,
    });

    const currentPath = pathName;
    const newPath = currentPath.replace(/\/[^/]*$/, `/prearrival/${newPart}`);
    router.push(newPath);
    // handleRedirect(newPath);
  };

  const sanitizedNode1 = DOMPurify.sanitize(updatedDescription, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString1 = sanitizedNode1.innerHTML;

  const [brandCdSetPage, setBrandCdSetPage] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  useEffect(() => {
    if (props?.brandCode) {
      if (props?.property_config?.non_branded_flg === 'Y') {
        setBrandCdSetPage(brandColorSchemeGenerator('NBF'));
      } else {
        setBrandCdSetPage(brandColorSchemeGenerator(props?.brandCode));
      }
      setFormData({
        ...formData,
        marshaCode: marshaCode,
        confirmationNumber: confirmationNumber1,
      });
    } else {
      if (formData?.property_config?.non_branded_flg === 'Y') {
        setBrandCdSetPage(brandColorSchemeGenerator('NBF'));
      } else {
        setBrandCdSetPage(brandColorSchemeGenerator(formData?.property_config?.brand_cd || formData?.brandCode));
      }
    }
    if (props?.backgroundImage) {
      setBackgroundImageUrl(props?.backgroundImage);
    } else {
      setBackgroundImageUrl(formData?.property_config?.background_image_url);
    }
    if (props?.reservation?.customerFirstName && props?.reservation?.customerLastName) {
      const firstName = capitalize(props.reservation.customerFirstName);
      const lastName = capitalize(props.reservation.customerLastName);
      setCustomerName(`${firstName} ${lastName}`);
    }
  }, [
    props?.backgroundImage,
    formData?.property_config?.background_image_url,
    props?.brandCode,
    formData?.property_config?.brand_cd,
    props?.reservation,
  ]);

  useEffect(() => {
    if (
      props?.isAuthorMode ||
      props?.reservation?.departure ||
      props?.reservation?.arrival ||
      props?.model?.welcomeText ||
      formData?.property_config?.brand_cd
    ) {
      setIsLoading(false);
    }
  }, [
    props?.isAuthorMode,
    props?.reservation?.departure,
    props?.reservation?.arrival,
    props?.model?.welcomeText,
    formData?.property_config?.brand_cd,
  ]);

  return (
    <div className="content-page">
      {isLoading ? (
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={brandCdSetPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Review"
          formName="Prearrival Travel Form"
          env_api_tile_error_info=""
          prearrival_form_amenities_added_to_plan={formData.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
        >
          <StyledWelcomeDetail data-testid="welcome-detail" data-component-name="o-account-welcomeDetail">
            <SurveyWrapperInner beforeSuveryWrapperInner="" afterSuveryWrapperInner="">
              <div id="welcome-modal">
                <div className="container">
                  <div className="row">
                    <div className="col-12">
                      <div className="welcome-page-wrapper">
                        <div className="d-flex top-right-content">
                          <p className="skeleton-loader" style={{ width: '200px', height: '20px' }}></p>
                          <p className="skeleton-loader pl-2" style={{ width: '200px', height: '20px' }}></p>
                        </div>
                        <div className="mb-3 skeleton-loader" style={{ width: '90%', height: '28px' }}></div>
                        <div className="mb-3 skeleton-loader" style={{ width: '80%', height: '35px' }}></div>
                        <div className="content-sec mb-4" data-testid="UpdateDescription">
                          <div className="skeleton-loader" style={{ width: '100%', height: '90px' }}></div>
                        </div>
                        <div className="navigation-btns d-flex flex-column align-items-center">
                          <div
                            className="skeleton-loader"
                            style={{
                              width: '180px',
                              height: '48px',
                              borderRadius: '6px',
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SurveyWrapperInner>
          </StyledWelcomeDetail>
        </SurveyWrapper>
      ) : (
        <SurveyWrapper
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={brandCdSetPage}
          marshaCode={marshaCode}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Welcome"
          formName="Prearrival Travel Form"
          env_api_tile_error_info={errorInfo}
          prearrival_form_amenities_added_to_plan={formData?.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          beforeSuveryWrapperOuter={props?.model?.beforeSuveryWrapperOuter}
          afterSuveryWrapperOuter={props?.model?.afterSuveryWrapperOuter}
        >
          {!isShowError ? (
            <StyledWelcomeDetail data-testid="welcome-detail" data-component-name="o-account-welcomeDetail">
              <SurveyWrapperInner
                beforeSuveryWrapperInner={props?.model?.beforeSuveryWrapperInner}
                afterSuveryWrapperInner={props?.model?.afterSuveryWrapperInner}
              >
                <div id="welcome-modal">
                  <div className="container">
                    <div className="row">
                      <div className="col-12">
                        <div className="welcome-page-wrapper">
                          <div className="d-flex top-right-content">
                            <p>
                              <span className="bold">Arrival: </span>
                              {dayjs(props?.reservation?.arrival).format('MMM D, YYYY')}
                            </p>

                            <p className="pl-2">
                              <span className="bold">Departure: </span>
                              {dayjs(props?.reservation?.departure).format('MMM D, YYYY')}
                            </p>
                          </div>
                          <h1 className="mb-3">
                            {welcomeI18nText}, <b>{customerName}</b>
                          </h1>

                          <div className="content-sec mb-4">
                            <Text
                              element={Types.tags.div}
                              fontSize={Types.size.medium}
                              dangerouslySetInnerHTML={{ __html: fragmentString1 }}
                              customClass=""
                              data-testid="UpdateDescription"
                            ></Text>
                          </div>
                          <div className="d-flex justify-content-center align-items-center">
                            <Button
                              className="m-button-m m-button-primary p-2  begin-button custom-btn mx-0 justify-content-center align-items-center flex-column mr-5 px-2 py-3"
                              buttonCopy="Begin Planning"
                              callback={() => handleNextPage('guest-details.mi')}
                            ></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SurveyWrapperInner>
            </StyledWelcomeDetail>
          ) : (
            <ErrorDetail
              hotelEmail={props.hotelEmail}
              hotelPhoneNumber={props.hotelPhoneNumber}
              confirmationNumber={props?.reservation?.confirmationNumber}
              errorVal={errorMsg}
            />
          )}
        </SurveyWrapper>
      )}
    </div>
  );
};

export const WelcomeWrapperConfig = {
  emptyLabel: 'WelcomeDetail',
  isEmpty: false,
  resourceType: `mi-aem-martech/components/welcomedetail`,
};
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const WelcomeDetailEditable = (props: any) => {
  return (
    <EditableComponent {...props} config={WelcomeWrapperConfig}>
      <WelcomeDetail {...props} />
    </EditableComponent>
  );
};
